"use client";

import { Button, Column, Feedback, Heading, Input, PasswordInput, SmartLink, Text } from "@once-ui-system/core";
import { useState } from "react";
import { AuthFrame, FieldLabel, type PlatformProvider, SocialButtons } from "@/components/auth/AuthFrame";

export function SignupForm({ providers }: { providers: PlatformProvider[] }) {
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    const body = {
      organization_name: String(f.get("organization_name") ?? "").trim(),
      full_name: String(f.get("full_name") ?? "").trim(),
      email: String(f.get("email") ?? "").trim(),
      password: String(f.get("password") ?? ""),
    };
    if (!body.organization_name || !body.full_name || !body.email || !body.password) {
      return setError("Fill in every field to create your workspace.");
    }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/api/auth/signup", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(body),
      });
      if (res.ok) return window.location.assign("/dashboard?welcome=1");
      const data = await res.json().catch(() => null);
      setError(data?.error?.message ?? "Could not create your workspace.");
    } catch {
      setError("The service is unreachable. Please try again shortly.");
    }
    setBusy(false);
  };

  return (
    <AuthFrame
      topAction={
        <Button variant="tertiary" size="s" href="/login">
          Sign in
        </Button>
      }
    >
      <Column gap="8" horizontal="center">
        <Heading as="h1" variant="heading-strong-xl" align="center">
          Create your workspace
        </Heading>
        <Text variant="body-default-s" onBackground="neutral-weak" align="center">
          You'll be its owner. Invite your team and connect your company SSO afterwards.
        </Text>
      </Column>

      {error && <Feedback variant="danger" description={error} />}

      <SocialButtons providers={providers} intent="signup" />

      <form onSubmit={submit} noValidate>
        <Column gap="16">
          <Column gap="8">
            <FieldLabel htmlFor="organization_name">Organization name</FieldLabel>
            <Input id="organization_name" name="organization_name" size="s" placeholder="Acme Inc." autoComplete="organization" />
          </Column>
          <Column gap="8">
            <FieldLabel htmlFor="full_name">Your name</FieldLabel>
            <Input id="full_name" name="full_name" size="s" placeholder="Jane Doe" autoComplete="name" />
          </Column>
          <Column gap="8">
            <FieldLabel htmlFor="email">Work email</FieldLabel>
            <Input
              id="email"
              name="email"
              type="email"
              size="s"
              placeholder="name@company.com"
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
            />
          </Column>
          <Column gap="8">
            <FieldLabel htmlFor="password">Password</FieldLabel>
            <PasswordInput
              id="password"
              name="password"
              size="s"
              placeholder="At least 12 characters"
              autoComplete="new-password"
              description="Mix at least three of: lowercase, uppercase, digits, symbols."
            />
          </Column>
          <Button type="submit" fillWidth loading={busy}>
            Create workspace
          </Button>
        </Column>
      </form>

      <Text variant="body-default-s" onBackground="neutral-weak" align="center">
        Already have an account? <SmartLink href="/login">Sign in</SmartLink>
      </Text>
    </AuthFrame>
  );
}
